// ─────────────────────────────────────────────────────────────────────────────
// FtuePresenterService
// ─────────────────────────────────────────────────────────────────────────────
// Decides what shows, now. Feature code only ever says "this condition just
// happened" (`request`); every rule about whether that becomes a visible card
// lives here:
//
//   • tips off in Settings                     → nothing
//   • already seen at this version              → nothing
//   • prerequisite tip not seen yet              → nothing
//   • budget: 1 per page view                   → nothing, stays eligible
//   • cooldown after a dismissal                → nothing, stays eligible
//   • a higher-priority tip is on screen        → nothing, stays eligible
//
// "Stays eligible" is the load-bearing part: nothing is marked seen until the
// user actually dismisses a card they were shown.
//
// Deliberately DOM-free: anchor resolution and rendering belong to
// <ui-ftue-layer>, which calls `abandon()` when an anchor never appears.
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { FtueStateStore } from './ftue-state.store';
import { FTUE_TIPS, findFtueTip } from './ftue-tip.registry';
import { FtueDismissReason, IFtuePresentation, IFtueTip, IFtueUserState } from './ftue.models';

/**
 * One tip per page view: a single order can trip several conditions at once, and
 * without a cap the first real order becomes a slideshow. This is the whole of
 * the budget.
 *
 * There was a second cap — three per session — removed deliberately. It did not
 * reduce how many cards a user ever sees, only how long they took to arrive:
 * every tip fires once per user for life, so the registry itself is the lifetime
 * bound. What the session cap did do was throttle by tab lifetime rather than by
 * anything the user would recognise, so reloading the page handed out a fresh
 * allowance while keeping one tab open all day did not — backwards, since the
 * steady user is the one it penalised.
 *
 * The slideshow is prevented by this cap, by the cooldown below, and by nothing
 * ever firing twice. If the registry grows enough that a first session starts to
 * feel like a tour, pace it here rather than by reinstating a hidden per-tab
 * allowance.
 */
const MAX_TIPS_PER_PAGE_VIEW = 1;

/**
 * Quiet period after a dismissal. Stops a second tip from appearing under the
 * cursor of the click that closed the first one — including on the page the user
 * clicks through to. Short on purpose: a minute of silence would swallow the tip
 * that the order you just opened actually needed.
 *
 * A strictly more important tip ignores it, see `cooldownPriority`.
 */
const COOLDOWN_AFTER_DISMISS_MS = 10_000;

@Injectable({ providedIn: 'root' })
export class FtuePresenterService implements OnDestroy {

  /** The card to render, or `null`. */
  readonly presentation$: Observable<IFtuePresentation | null>;

  /** Persisted state, re-emitted on every change (Settings page, inspector). */
  readonly state$: Observable<IFtueUserState>;

  private readonly presentationSubject = new BehaviorSubject<IFtuePresentation | null>(null);
  private readonly stateSubject: BehaviorSubject<IFtueUserState>;
  private readonly destroy$ = new Subject<void>();

  /** Path of the page the current page view belongs to, without query string. */
  private currentPath: string;

  private shownThisPageView = 0;
  private cooldownUntil = 0;

  /**
   * Priority of the tip whose dismissal started the current cooldown. The quiet
   * period protects against nagging with something equally or less important —
   * never against a tip that needs a decision.
   */
  private cooldownPriority = 0;

  constructor(
    private readonly store: FtueStateStore,
    private readonly router: Router,
  ) {
    this.currentPath = pathOf(this.router.url);
    this.stateSubject = new BehaviorSubject<IFtueUserState>(this.store.read());
    this.state$ = this.stateSubject.asObservable();
    this.presentation$ = this.presentationSubject.asObservable();

    // A page view ends when the user leaves for a different page. Two things
    // that look equivalent and are not:
    //
    //  • NavigationEnd instead of NavigationStart — route components are created
    //    *during* a navigation, so a tip requested from `ngOnInit` (the natural
    //    place for "this order has no company") is already on screen when
    //    NavigationEnd arrives. Clearing there wiped the card that navigation had
    //    just produced; it survived only when the API answered slowly enough to
    //    land after the event, which is why it appeared on a page refresh and
    //    never on an in-app click.
    //
    //  • any navigation instead of a change of path — a list syncs its page,
    //    page size and search term into the query string, and each of those is a
    //    navigation. Treating them as "the user left" closed the card a moment
    //    after it opened, at random, depending on which won the race.
    this.router.events
      .pipe(
        filter((event): event is NavigationStart => event instanceof NavigationStart),
        filter((event) => pathOf(event.url) !== this.currentPath),
        takeUntil(this.destroy$),
      )
      .subscribe((event) => {
        this.currentPath = pathOf(event.url);
        this.shownThisPageView = 0;
        if (this.presentationSubject.value) {
          // Leaving the page takes the anchor with it. Not seen: the user walked
          // away from the card rather than dealing with it.
          this.releaseBudget();
          this.clear();
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Firing ─────────────────────────────────────────────────────────────────

  /**
   * A trigger condition just occurred. Safe to call on every render: the
   * presenter is the one that knows whether it means anything.
   */
  request(tipId: string): void {
    const tip = findFtueTip(tipId);
    if (!tip) {
      return;
    }

    const active = this.presentationSubject.value;
    if (active) {
      // A preview is a deliberate user action — never interrupt it. Otherwise
      // the higher priority wins and the loser keeps its "not seen" state.
      if (active.isPreview || tip.priority <= active.tip.priority) {
        return;
      }
    }

    // The open card's own slot is about to be given back, so it must not count
    // against the tip that is replacing it — otherwise the budget would always
    // hand the page view to whichever condition happened to fire first.
    if (!this.isEligible(tip, active && !active.isPreview ? 1 : 0)) {
      return;
    }

    if (active) {
      this.releaseBudget();
    }
    this.present(tip, false);
  }

  /** Settings → Preview. Ignores seen state, budget, cooldown and the toggle. */
  preview(tipId: string): void {
    const tip = findFtueTip(tipId);
    if (tip) {
      this.present(tip, true);
    }
  }

  // ── Ending ─────────────────────────────────────────────────────────────────

  /**
   * The user dealt with the card — button, X, click-outside or Esc. All four
   * count as seen; the card said so before they clicked.
   */
  dismiss(_reason: FtueDismissReason): void {
    const active = this.presentationSubject.value;
    if (!active) {
      return;
    }
    if (!active.isPreview) {
      this.markSeen(active.tip);
      this.cooldownUntil = Date.now() + COOLDOWN_AFTER_DISMISS_MS;
      this.cooldownPriority = active.tip.priority;
    }
    this.clear();
  }

  /**
   * The card could not be shown after all — its anchor never resolved, or it
   * scrolled out of reach. Not seen, no cooldown, budget refunded: the tip must
   * get another chance rather than being silently burned.
   */
  abandon(): void {
    if (!this.presentationSubject.value) {
      return;
    }
    this.releaseBudget();
    this.clear();
  }

  // ── Settings surface ───────────────────────────────────────────────────────

  get tipsEnabled(): boolean {
    return this.stateSubject.value.tipsEnabled;
  }

  setTipsEnabled(enabled: boolean): void {
    this.save({ ...this.stateSubject.value, tipsEnabled: enabled });
    if (!enabled) {
      // Turning tips off from inside a tip has to take that tip with it.
      this.clear();
    }
  }

  /** Forgets every tip, so all of them behave as never seen. */
  resetHistory(): void {
    this.save({ ...this.stateSubject.value, seen: {} });
    this.shownThisPageView = 0;
    this.cooldownUntil = 0;
    this.cooldownPriority = 0;
  }

  /** Seen at its current version — a version bump flips this back to false. */
  hasSeen(tip: IFtueTip): boolean {
    return this.stateSubject.value.seen[tip.id] === tip.version;
  }

  /**
   * Past its `expiresOn` date. Announcements retire on their own so that
   * removing them is not a chore somebody has to remember two quarters later.
   */
  hasExpired(tip: IFtueTip): boolean {
    if (!tip.expiresOn) {
      return false;
    }
    const expiry = Date.parse(tip.expiresOn);
    return !Number.isNaN(expiry) && Date.now() >= expiry;
  }

  /** For the "Tips (n) · m seen" header of the inventory table. */
  get seenCount(): number {
    return FTUE_TIPS.filter((tip) => this.hasSeen(tip)).length;
  }

  // ── Internals ──────────────────────────────────────────────────────────────

  /**
   * `refundableSlots` discounts budget the caller is about to release — the one
   * slot held by a card being replaced by a higher-priority tip.
   */
  private isEligible(tip: IFtueTip, refundableSlots = 0): boolean {
    return (
      this.tipsEnabled &&
      !this.hasSeen(tip) &&
      !this.hasExpired(tip) &&
      !this.isInCooldownFor(tip) &&
      !this.hasUnmetPrerequisite(tip) &&
      this.shownThisPageView - refundableSlots < MAX_TIPS_PER_PAGE_VIEW
    );
  }

  /**
   * Waiting on a lesson the user has not had yet. Checked by presence in `seen`
   * rather than by version: the prerequisite is "has this concept been taught",
   * and a copy edit to the earlier tip must not re-lock the later one for
   * everybody who already learned it.
   */
  private hasUnmetPrerequisite(tip: IFtueTip): boolean {
    return !!tip.requires && this.stateSubject.value.seen[tip.requires] === undefined;
  }

  /** Quiet after a dismissal, unless this tip matters more than the last one. */
  private isInCooldownFor(tip: IFtueTip): boolean {
    return Date.now() < this.cooldownUntil && tip.priority <= this.cooldownPriority;
  }

  private present(tip: IFtueTip, isPreview: boolean): void {
    if (!isPreview) {
      this.shownThisPageView++;
    }
    this.presentationSubject.next({ tip, isPreview });
  }

  /** Undoes what `present` counted, for a tip that never made it to the screen. */
  private releaseBudget(): void {
    const active = this.presentationSubject.value;
    if (active && !active.isPreview) {
      this.shownThisPageView = Math.max(0, this.shownThisPageView - 1);
    }
  }

  private clear(): void {
    this.presentationSubject.next(null);
  }

  private markSeen(tip: IFtueTip): void {
    this.save({
      ...this.stateSubject.value,
      seen: { ...this.stateSubject.value.seen, [tip.id]: tip.version },
    });
  }

  private save(state: IFtueUserState): void {
    this.store.write(state);
    this.stateSubject.next(state);
  }
}

/** A URL's path, with query string and fragment stripped. */
function pathOf(url: string | undefined): string {
  return (url ?? '').split('?')[0].split('#')[0];
}
